import { useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Paperclip, 
  Upload, 
  Download, 
  Trash2, 
  File,
  FileText,
  FileImage,
  FileSpreadsheet,
  Loader2,
  X
} from "lucide-react";
import api from "@/services/api";

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

// File icon based on type
function getFileIcon(contentType) {
  if (!contentType) return File;
  if (contentType.startsWith("image/")) return FileImage;
  if (contentType.includes("pdf") || contentType.includes("document")) return FileText;
  if (contentType.includes("sheet") || contentType.includes("excel") || contentType.includes("csv")) return FileSpreadsheet;
  return File;
}

// Format file size
function formatFileSize(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
}

export default function FileAttachments({ taskId, attachments = [], onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deleting, setDeleting] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  const handleFileSelect = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate file sizes
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} is too large. Maximum size is 25MB.`);
        return;
      }
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append("file", file);

        await api.post(`/api/tasks/${taskId}/attachments`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              ((i + progressEvent.loaded / progressEvent.total) / files.length) * 100
            );
            setUploadProgress(progress);
          },
        });

        toast.success(`Uploaded ${file.name}`);
      }

      // Refresh attachments
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Clear the input
      e.target.value = "";
    }
  }, [taskId, onUpdate]);

  const handleDownload = useCallback(async (attachment) => {
    try {
      const response = await api.get(`/api/attachments/${attachment.file_id}`, {
        responseType: "blob",
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download file");
    }
  }, []);

  const confirmDelete = useCallback((attachment) => {
    setFileToDelete(attachment);
    setDeleteDialogOpen(true);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!fileToDelete) return;

    setDeleting(fileToDelete.file_id);
    try {
      await api.delete(`/api/tasks/${taskId}/attachments/${fileToDelete.file_id}`);
      toast.success(`Deleted ${fileToDelete.filename}`);
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete file");
    } finally {
      setDeleting(null);
      setDeleteDialogOpen(false);
      setFileToDelete(null);
    }
  }, [taskId, fileToDelete, onUpdate]);

  return (
    <div className="space-y-3" data-testid="file-attachments">
      {/* Upload Section */}
      <div className="flex items-center gap-2">
        <label className="cursor-pointer">
          <input
            type="file"
            multiple
            onChange={handleFileSelect}
            disabled={uploading}
            className="hidden"
            data-testid="file-upload-input"
          />
          <Button
            variant="outline"
            size="sm"
            disabled={uploading}
            asChild
          >
            <span>
              {uploading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Upload Files
            </span>
          </Button>
        </label>
        <span className="text-xs text-muted-foreground">
          Max 25MB per file
        </span>
      </div>

      {/* Upload Progress */}
      {uploading && (
        <div className="space-y-1">
          <Progress value={uploadProgress} className="h-2" />
          <p className="text-xs text-muted-foreground">
            Uploading... {uploadProgress}%
          </p>
        </div>
      )}

      {/* Attachments List */}
      {attachments.length > 0 ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Paperclip className="h-4 w-4" />
            <span>{attachments.length} attachment{attachments.length !== 1 && "s"}</span>
          </div>
          <div className="grid gap-2">
            {attachments.map((attachment) => {
              const FileIcon = getFileIcon(attachment.content_type);
              const isDeleting = deleting === attachment.file_id;

              return (
                <div
                  key={attachment.file_id}
                  className="flex items-center justify-between p-2 rounded-md border bg-muted/30 hover:bg-muted/50 transition-colors"
                  data-testid={`attachment-${attachment.file_id}`}
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FileIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate" title={attachment.filename}>
                        {attachment.filename}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(attachment.size)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleDownload(attachment)}
                      title="Download"
                      data-testid={`download-${attachment.file_id}`}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => confirmDelete(attachment)}
                      disabled={isDeleting}
                      title="Delete"
                      data-testid={`delete-attachment-${attachment.file_id}`}
                    >
                      {isDeleting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground py-2">
          No attachments yet
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Attachment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{fileToDelete?.filename}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
